import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { flushSync } from 'react-dom';
import { X, Download, Upload, Camera } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { usePhotoContext } from '@/contexts/PhotoContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useUpdateCurrentUser } from '@/contexts/NetworkDataContext';
import { useProfilePhoto } from '@/hooks/useProfilePhoto';
import SimpleReplacePhotoDialog from '@/components/SimpleReplacePhotoDialog';
import { Capacitor } from '@capacitor/core';
import { Camera as CapacitorCamera, CameraResultType, CameraSource } from '@capacitor/camera';
import PhotoPreviewEmpty from '@/components/PhotoPreviewEmpty';
import { resolveDocumentDirection } from '@/i18n/languages';
import {
  v258SetPreview,
  v258StartUpload,
  v258SetUploaded
} from '@/lib/photoStore';

/**
 * V213.3: Get current profile photo URL with priority:
 * 1. photo_urls[0] (Cloudinary from Supabase) - BEST
 * 2. Build from profile_image_public_id (Cloudinary)
 * 3. user_metadata.avatar_url (Google) - FALLBACK ONLY
 * 
 * @param user - User object with photo_urls, profile_image_public_id, or user_metadata
 * @returns Cloudinary URL (preferred) or Google URL (fallback), or null
 */
interface ProfilePhotoSource {
  photo_urls?: unknown;
  user_metadata?: {
    avatar_url?: unknown;
    picture?: unknown;
  };
}

function getCurrentProfilePhotoUrl(user: unknown): string | null {
  // Handle case where a URL string is passed directly
  if (typeof user === 'string') {
    return user || null;
  }

  if (!user || typeof user !== 'object') return null;
  const profileSource = user as ProfilePhotoSource;

  // Priority 1: photo_urls[0] from DB (Cloudinary secure_url typically includes /v<version>/)
  // NOTE: users.photo_urls is Json in Supabase types, so it may come back as:
  // - string[]
  // - a single string URL
  // - a JSON-stringified array
  const rawPhotoUrls = profileSource.photo_urls;
  let firstUrl: unknown = null;

  if (Array.isArray(rawPhotoUrls)) {
    firstUrl = rawPhotoUrls[0];
  } else if (typeof rawPhotoUrls === 'string') {
    // Could be either a URL or a JSON string
    if (rawPhotoUrls.startsWith('http')) {
      firstUrl = rawPhotoUrls;
    } else {
      try {
        const parsed = JSON.parse(rawPhotoUrls);
        if (Array.isArray(parsed)) firstUrl = parsed[0];
      } catch {
        // ignore
      }
    }
  }

  if (typeof firstUrl === 'string' && firstUrl.length > 0) {
    // Prefer Cloudinary URLs (versioned URL is best to avoid stale CDN/browser caches)
    if (firstUrl.includes('res.cloudinary.com')) {
      return firstUrl;
    }
  }

  // Priority 2: Google OAuth fallback ONLY (if photo_urls isn't usable)
  // V274.2: Ensure high-resolution Google image (s2048 instead of s96)
  const google = profileSource.user_metadata?.avatar_url || profileSource.user_metadata?.picture;
  if (typeof google === 'string') {
    // V274.2: Convert to high resolution if it's a Google URL
    if (google.includes('googleusercontent.com')) {
      // Replace any size parameter with s2048 for high resolution
      return google.replace(/=s\d+-c/, '=s2048-c').replace(/=s\d+/, '=s2048');
    }
    return google;
  }

  // FINAL fallback: If photo_urls exists but wasn't Cloudinary, use it
  if (typeof firstUrl === 'string' && firstUrl.length > 0) {
    return firstUrl;
  }

  // IMPORTANT: Do NOT build a non-versioned Cloudinary URL from profile_image_public_id.
  // That URL is stable and can remain visually stale due to CDN caching when overwrite=true.
  return null;
}

/**
 * V214: Nuclear cache invalidation - clears all possible caches for immediate photo update
 */
const nuclearCacheInvalidation = async (userId: string) => {
  console.log('💥 V214: Nuclear cache invalidation for user:', userId);

  try {
    // Clear localStorage caches - expanded list
    const cacheKeys = [
      'blissnet_unified_photo_cache',
      'blissnet_photo_cache',
      'photo_cache',
      `photos_${userId}`,
      'userPhotos',
      'networkData',
      'currentUser',
      'userData',
      `user_${userId}`
    ];

    cacheKeys.forEach(key => {
      try {
        const cached = localStorage.getItem(key);
        if (cached) {
          const data = JSON.parse(cached);
          if (typeof data === 'object' && data[userId]) {
            delete data[userId];
            localStorage.setItem(key, JSON.stringify(data));
          } else if (key.includes(userId)) {
            localStorage.removeItem(key);
          }
        }
      } catch (e) {
        localStorage.removeItem(key);
      }
    });

    // V167.INVITE.FIX: Clear sessionStorage EXCEPT invite data
    const inviteData = {
      pendingInviteCode: sessionStorage.getItem('pendingInviteCode'),
      pendingInviteContext: sessionStorage.getItem('pendingInviteContext'),
      showPostOnboardingInvitePopup: sessionStorage.getItem('showPostOnboardingInvitePopup'),
      postOnboardingInviteData: sessionStorage.getItem('postOnboardingInviteData'),
      inviteCodeTimestamp: sessionStorage.getItem('inviteCodeTimestamp'),
      inviteChoice: sessionStorage.getItem('inviteChoice')
    };

    sessionStorage.clear();

    // Restore invite data
    Object.entries(inviteData).forEach(([key, value]) => {
      if (value) sessionStorage.setItem(key, value);
    });

    // Clear browser cache for images
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(name => caches.delete(name))
      );
    }

    console.log('💥 V214: Nuclear cache invalidation complete');
  } catch (error) {
    console.warn('V214: Nuclear cache invalidation partial failure:', error);
  }
};

// V325: Removed dead extractUserIdFromPublicId - uploadWithVerify.ts handles verification

interface ProfilePhotoViewerProps {
  photoUrl: string;
  userName?: string;
  onClose: () => void;
  onEdit?: () => void;
  onPhotoUpdate?: (newPhotoUrl: string) => void;
  isOwnProfile?: boolean;
  userId?: string; // V150.17: Add userId for unified service
  initialFile?: File; // Android: Pre-selected file from camera to trigger replace dialog
  initialPreviewUrl?: string; // Android: Preview URL for initial file
  initialDataUrl?: string; // V276.3: Original dataUrl from camera (base64) - ready for upload, no FileReader needed!
}

// V150.17: UNIFIED PHOTO VIEWER - Updated to use unified service and photo context
export const ProfilePhotoViewer: React.FC<ProfilePhotoViewerProps> = ({
  photoUrl,
  userName = 'User',
  onClose,
  onEdit,
  onPhotoUpdate,
  isOwnProfile = false,
  userId,
  initialFile, // Android: Pre-selected file from camera
  initialPreviewUrl, // Android: Preview URL for initial file
  initialDataUrl // V276.3: Original dataUrl from camera (base64)
}) => {
  const { t, i18n } = useTranslation();
  const direction = resolveDocumentDirection(i18n.resolvedLanguage || i18n.language);

  // V276.3: Log props received
  console.log('[V276.3] ════════════════════════════════════════');
  console.log('[V276.3] ProfilePhotoViewer rendered');
  console.log('[V276.3] initialDataUrl:', !!initialDataUrl, 'length:', initialDataUrl?.length || 0);
  console.log('[V276.3] initialFile:', !!initialFile);
  console.log('[V276.3] initialPreviewUrl:', !!initialPreviewUrl);
  console.log('[V276.3] ════════════════════════════════════════');

  // V329: Use unified photo hook - userId ?? null for correct typing
  const { photoUrl: unifiedPhotoUrl, updatePhoto: updateUnifiedPhoto } = useProfilePhoto(userId ?? null, null);

  // V152.9: NUCLEAR MINIMAL STATE - No reload loops, direct prop usage
  const [isUploading, setIsUploading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [newImageFile, setNewImageFile] = useState<File | null>(initialFile || null);
  const [newImagePreview, setNewImagePreview] = useState<string | null>(initialPreviewUrl || null);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const navigate = useNavigate();
  const { updateUserPhoto } = usePhotoContext();
  const updateCurrentUser = useUpdateCurrentUser();
  // V250_VIEWER: Freeze viewer image src for duration of open to avoid canceled requests
  const viewerSrcAtOpen = useRef<string | null>(null);
  // V267.2: Stable key counter - only changes when new photo uploaded
  const imageKeyRef = useRef(0);

  // V263.6: Display name with enhanced fetch and persistence
  const [displayName, setDisplayName] = useState<string>('Loading...');
  const hasAttemptedFetch = useRef(false);
  
  // V254: Upload concurrency lock - prevents multiple uploads in flight
  const uploadInFlightRef = useRef<boolean>(false);
  // V254: Upload cycle counter for logging
  const uploadCycleRef = useRef<number>(0);
  // V254: Track upload in flight for UI (derived from ref)
  const [uploadInFlight, setUploadInFlight] = useState(false);
  
  // V275.4: CRITICAL FIX - Prop takes PRIORITY over hook
  // The parent component (Profile) has the most up-to-date URL from DB
  // The hook may have stale/cached data or the old Google avatar
  const displayPhotoUrl = photoUrl || unifiedPhotoUrl;
  
  // V275.4: Log which source is being used for debugging
  if (import.meta.env.DEV) {
    console.log('[V275.4] PhotoViewer URL sources:', {
      propPhotoUrl: photoUrl?.substring(0, 60),
      hookPhotoUrl: unifiedPhotoUrl?.substring(0, 60),
      selected: displayPhotoUrl?.substring(0, 60),
      usingProp: !!photoUrl
    });
  }

  // INSTANT_REFRESH: Local state for immediate UI update after upload (bypasses refetch lag)
  const [displayedPhotoUrl, setDisplayedPhotoUrl] = useState<string | null>(null);
  
  // V257: Track current publicId for img key (forces remount on new upload)
  const [currentPublicId, setCurrentPublicId] = useState<string | null>(null);

  // V246: Snapshot of current photo when dialog opens (not reactive)
  const [currentPhotoAtOpen, setCurrentPhotoAtOpen] = useState<string | null>(null);

  // V213.5: Viewer URL helpers.
  function stripCloudinaryTransforms(url: string) {
    if (!url || !url.includes('res.cloudinary.com') || !url.includes('/upload/')) return url;

    const [base, restWithQuery] = url.split('/upload/');
    if (!restWithQuery) return url;

    const [rest, query] = restWithQuery.split('?');
    const parts = rest.split('/');

    // If the first segment is NOT a version (v123...), it's a transform segment → remove it.
    const cleanedRest = parts[0]?.startsWith('v') ? rest : parts.slice(1).join('/');
    const rebuilt = `${base}/upload/${cleanedRest}`;

    return query ? `${rebuilt}?${query}` : rebuilt;
  }

  function getViewerImageSrc(url: string) {
    if (!url) return url;

    if (url === '__BLOB_CACHED__' || url.includes('__BLOB_CACHED__')) return '';

    // V329: data: and blob: pass through (cooked preview)
    if (url.startsWith('data:') || url.startsWith('blob:')) return url;

    if (url.includes('googleusercontent.com')) {
      return url.replace(/=s\d+(-c)?$/, '=s2048');
    }

    if (url.includes('res.cloudinary.com')) {
      const clean = stripCloudinaryTransforms(url);
      return clean.replace(
        '/upload/',
        '/upload/w_1080,h_1350,c_fill,g_faces,q_auto,f_auto/'
      );
    }

    return url;
  }

  // V258.3: Compute viewer src - trust PhotoStore, no blob filtering
  const selectedUrl = displayedPhotoUrl || displayPhotoUrl || '';
  const computedViewerSrc = (() => {
    if (!selectedUrl) return '';

    // V258.3: Trust PhotoStore - no blob URL rejection
    const src = getViewerImageSrc(selectedUrl);

    // V258.3: Log final render src for debugging
    const isBlob = src?.startsWith('blob:') ?? false;
    const isRemote = src?.startsWith('http') ?? false;
    if (import.meta.env.DEV) {
      console.log(`🟢 V258.3: computedViewerSrc:`, {
        selectedUrl: selectedUrl.substring(0, 80),
        finalSrc: src ? src.substring(0, 80) : 'empty',
        isBlob,
        isRemote,
        state: isBlob ? 'PREVIEWING' : (isRemote ? 'UPLOADED' : 'UNKNOWN')
      });
    }

    return src;
  })();

  // V329: Freeze ref for stable rendering - allow blob: and data: during preview
  useLayoutEffect(() => {
    if (computedViewerSrc && !computedViewerSrc.startsWith('blob:') && !computedViewerSrc.startsWith('data:')) {
      // Only freeze remote URLs (Cloudinary)
      if (displayedPhotoUrl && computedViewerSrc) {
        viewerSrcAtOpen.current = computedViewerSrc;
        if (import.meta.env.DEV) {
          console.log(`🟢 V258.3: Frozen viewer src (remote URL): ${computedViewerSrc.substring(0, 80)}`);
        }
      } else if (!viewerSrcAtOpen.current && computedViewerSrc) {
        viewerSrcAtOpen.current = computedViewerSrc;
        if (import.meta.env.DEV) {
          console.log(`🟢 V258.3: Frozen viewer src (initial load): ${computedViewerSrc.substring(0, 80)}`);
        }
      }
    } else if (computedViewerSrc && (computedViewerSrc.startsWith('blob:') || computedViewerSrc.startsWith('data:'))) {
      viewerSrcAtOpen.current = null;
    }
  }, [computedViewerSrc, displayedPhotoUrl]);

  // V256: Use frozen ref if available, otherwise use computed (ensures image shows on first render)
  // BUT: If displayedPhotoUrl is set (new upload), prefer computed to show new image
  const renderViewerSrc = (displayedPhotoUrl && computedViewerSrc) ? computedViewerSrc : (viewerSrcAtOpen.current || computedViewerSrc);

  // V255: Instrumentation - log render src for debugging
  useEffect(() => {
    if (renderViewerSrc) {
      console.log(`🖼️ V255: renderViewerSrc updated:`, {
        frozen: viewerSrcAtOpen.current?.substring(0, 80) || 'none',
        computed: computedViewerSrc?.substring(0, 80) || 'none',
        final: renderViewerSrc.substring(0, 80),
      });
    }
  }, [renderViewerSrc, computedViewerSrc]);

  // V248: Track preview blob URL for proper cleanup (one URL per action)
  const previewUrlRef = useRef<string | null>(null);

  // V249: Freeze dialog UI when confirming (prevents any reactive updates)
  const isClosingRef = useRef(false);

  // V250: Pending replace file for useEffect-based upload (after dialog unmounts)
  // V261.3: Changed to store dataUrl instead of File (File becomes stale on Android after dialog closes)
  const pendingReplaceFileRef = useRef<{
    dataUrl: string;  // V261.3: Store dataUrl directly (not File - File becomes stale on Android)
    previewUrl: string;
    userId: string;
  } | null>(null);

  // V248: Helper to wait for next paint (double RAF for portal/unmount guarantee)
  const nextPaint = (): Promise<void> => {
    return new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resolve();
        });
      });
    });
  };

  const CONVERSION_TIMEOUT_MS = 10000;
  const MAX_BLOB_BYTES = 8 * 1024 * 1024; // V329: 8MB limit (base64 expansion)

  // V327: Signature-based guard - allows re-processing when user picks a different file quickly
  const hasProcessedInitialRef = useRef<string | null>(null);

  /**
   * V329: Web-only fallback - FileReader for File, fetch for blob:.
   * NO content:// or file:// fetch - Samsung must use Camera.getPhoto(Base64).
   */
  const fileOrUriToDataUrl = async (
    file: File | null,
    previewUrl: string | null
  ): Promise<{ dataUrl: string; strategy: string } | null> => {
    const withTimeout = <T,>(p: Promise<T>): Promise<T> =>
      Promise.race([
        p,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Read timeout')), CONVERSION_TIMEOUT_MS)
        ),
      ]);

    const blobToDataUrl = (blob: Blob): Promise<string> =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (typeof reader.result === 'string' && reader.result.startsWith('data:image/')) {
            resolve(reader.result);
          } else {
            reject(new Error('Invalid data format'));
          }
        };
        reader.onerror = () => reject(new Error('FileReader failed'));
        reader.readAsDataURL(blob);
      });

    // V329: blob: only (web) - NO content:// or file:// fetch
    if (previewUrl && previewUrl.startsWith('blob:')) {
      try {
        const response = await withTimeout(fetch(previewUrl));
        const blob = await response.blob();
        const dataUrl = await blobToDataUrl(blob);
        return { dataUrl, strategy: 'fetch-blob' };
      } catch (e) {
        console.warn('[V329] Blob fetch failed:', e);
      }
    }

    // V329: FileReader (web File object only)
    if (file) {
      try {
        const dataUrl = await withTimeout(
          new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = () => reject(new Error('Fallback FileReader failed'));
            reader.readAsDataURL(file);
          })
        );
        return { dataUrl, strategy: 'direct-file-reader' };
      } catch (e) {
        console.error('[V324] All read strategies failed:', e);
      }
    }

    return null;
  };
  
  // V265.3: Removed cache-buster state - no longer needed
  // Cloudinary handles caching correctly

  // V250: Capture snapshot synchronously when dialog opens (NO reactive dependencies)
  // This ensures the snapshot never changes even if displayPhotoUrl updates while dialog is open
  const captureSnapshot = (url: string | null) => {
    setCurrentPhotoAtOpen(url);
    // V265.3: Removed cache-buster setter - not needed
    if (import.meta.env.DEV && url) {
      console.log('✅ V265.3: Snapshot captured for dialog:', url.substring(0, 80));
    }
  };

  // V276.3: Store dataUrl from camera (passed directly, no FileReader needed!)
  const [newImageDataUrl, setNewImageDataUrl] = useState<string | null>(initialDataUrl || null);

  // V327: Atomic Detachment with signature-based deduplication and race-safe unmount
  React.useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    // V327: Signature for deduplication - allows re-processing when user picks different file
    const currentSignature = initialDataUrl
      ? `dataurl-${initialDataUrl.length}`
      : initialPreviewUrl || (initialFile ? `${initialFile.name}-${initialFile.size}` : null);

    // Reset when props are cleared
    if (!currentSignature) {
      hasProcessedInitialRef.current = null;
      return;
    }

    // V327: Signature deduplication guard - skip if same selection already processed
    if (hasProcessedInitialRef.current === currentSignature) return;
    hasProcessedInitialRef.current = currentSignature;

    const initializeImage = async () => {
      let cooked: string | null = null;

      try {
        if (initialDataUrl) {
          cooked = initialDataUrl;
        } else if (initialPreviewUrl?.startsWith('content://') || initialPreviewUrl?.startsWith('file://')) {
          // V329: Never fetch content:// - use Upload button with native picker instead
          console.debug('[V329] invalidDataUrl: received content:// or file:// - use Upload button');
          if (alive) toast.error('Please use the Upload button to select from gallery.', { id: 'read-error' });
        } else if (initialPreviewUrl?.startsWith('blob:')) {
          const res = await fetch(initialPreviewUrl, { signal: controller.signal });
          const blob = await res.blob();
          cooked = await new Promise<string>((resolve, reject) => {
            const r = new FileReader();
            r.onloadend = () => resolve(r.result as string);
            r.onerror = () => reject(new Error('FileReader failed'));
            r.readAsDataURL(blob);
          });
        } else if (initialFile) {
          cooked = await new Promise<string>((resolve, reject) => {
            const r = new FileReader();
            r.onloadend = () => resolve(r.result as string);
            r.onerror = () => reject(new Error('FileReader failed'));
            r.readAsDataURL(initialFile);
          });
        }
      } catch (e: unknown) {
        const err = e as Error;
        if (err?.name !== 'AbortError' && alive) {
          console.error('[V329] Initialize image failed:', err);
          toast.error('Could not read image. Please try again.', { id: 'read-error' });
        }
      }

      if (cooked && alive && cooked.startsWith('data:image/')) {
        setNewImageDataUrl(cooked);
        setNewImagePreview(cooked);
        setNewImageFile(initialFile || null);
        const snapshotUrl = photoUrl || displayedPhotoUrl || displayPhotoUrl || null;
        if (snapshotUrl) captureSnapshot(snapshotUrl);
        setShowConfirmation(true);
      }
    };

    initializeImage();
    return () => {
      alive = false;
      controller.abort();
    };
    // This initialization is intentionally keyed only to incoming selection props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDataUrl, initialFile, initialPreviewUrl]);

  // V248: Cleanup blob URLs on component unmount (final cleanup)
  useEffect(() => {
    return () => {
      // Revoke preview blob URL on unmount if still exists
      if (previewUrlRef.current && previewUrlRef.current.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(previewUrlRef.current);
          console.log('✅ V248: Cleaned up preview blob URL on unmount');
        } catch (error) {
          console.warn('⚠️ V248: Failed to revoke preview blob URL (non-critical):', error);
        }
        previewUrlRef.current = null;
      }

      // V250_VIEWER: Reset viewer src snapshot on unmount/close
      viewerSrcAtOpen.current = null;
    };
  }, []); // Only on unmount

  // V263.6: Fetch user name from database with enhanced logging and persistence
  useEffect(() => {
    // Prevent multiple fetch attempts
    if (hasAttemptedFetch.current) {
      console.log('[V263.6 NAME] Already attempted fetch, skipping');
      return;
    }

    const fetchUserName = async () => {
      console.log('═══════════════════════════════════════════════════════');
      console.log('[V263.6 NAME] 🔍 FETCH ATTEMPT STARTED');
      console.log('═══════════════════════════════════════════════════════');
      console.log('[V263.6 NAME] Props received:');
      console.log('[V263.6 NAME]   - userName:', userName);
      console.log('[V263.6 NAME]   - userId:', userId);
      console.log('[V263.6 NAME] Current displayName:', displayName);
      console.log('───────────────────────────────────────────────────────');

      // Mark as attempted
      hasAttemptedFetch.current = true;

      // CRITICAL CHECK: userId must exist
      if (!userId || userId.trim() === '') {
        console.error('[V263.6 NAME] ❌ CRITICAL: No userId provided!');
        console.error('[V263.6 NAME] Parent component must pass userId prop');
        console.error('[V263.6 NAME] ProfilePhotoViewer cannot fetch without userId');
        console.error('[V263.6 NAME] Falling back to userName prop:', userName || 'User');
        setDisplayName(userName || 'User');
        console.log('═══════════════════════════════════════════════════════');
        return;
      }

      console.log('[V263.6 NAME] ✅ userId is valid:', userId);

      // Check if userName prop is already valid
      if (userName &&
          userName !== 'profile' &&
          userName !== 'User' &&
          userName !== 'Loading...' &&
          userName.trim().length > 0) {
        console.log('[V263.6 NAME] ✅ Valid userName prop provided:', userName);
        console.log('[V263.6 NAME] Using prop value (no database fetch needed)');
        setDisplayName(userName);
        console.log('═══════════════════════════════════════════════════════');
        return;
      }

      console.log('[V263.6 NAME] ⚠️ Invalid userName prop:', userName);
      console.log('[V263.6 NAME] Need to fetch from database');
      console.log('───────────────────────────────────────────────────────');
      console.log('[V263.6 NAME] 🔄 Starting database query...');
      console.log('[V263.6 NAME] Querying users table for userId:', userId);

      try {
        const startTime = performance.now();

        const { data: authData } = await supabase.auth.getUser();
        let data: { id?: string; name?: string | null; email?: string | null } | null = null;
        let error: { message?: string; code?: string; details?: string } | null = null;
        if (authData?.user?.id === userId) {
          const r = await supabase.from('users').select('id, name, email').eq('id', userId).single();
          data = r.data;
          error = r.error;
        } else {
          const { rpcPeerUsersForViewer } = await import('@/lib/d001PeerUsersRpc');
          const { data: rows, error: e } = await rpcPeerUsersForViewer(supabase, [userId], { includePrefs: false });
          if (e) error = e;
          else data = rows?.[0] ? { id: rows[0].id, name: rows[0].name, email: '' } : null;
        }

        const fetchTime = Math.round(performance.now() - startTime);

        console.log('[V263.6 NAME] 📊 Database query completed');
        console.log('[V263.6 NAME] Query time:', fetchTime, 'ms');
        console.log('───────────────────────────────────────────────────────');

        if (error) {
          console.error('[V263.6 NAME] ❌ DATABASE ERROR:');
          console.error('[V263.6 NAME] Error message:', error.message);
          console.error('[V263.6 NAME] Error code:', error.code);
          console.error('[V263.6 NAME] Error details:', error.details);
          console.error('[V263.6 NAME] This might be an RLS policy issue');
          console.error('[V263.6 NAME] Falling back to:', userName || 'User');
          setDisplayName(userName || 'User');
          console.log('═══════════════════════════════════════════════════════');
          return;
        }

        if (!data) {
          console.error('[V263.6 NAME] ❌ NO DATA RETURNED');
          console.error('[V263.6 NAME] User might not exist in database');
          console.error('[V263.6 NAME] userId queried:', userId);
          console.error('[V263.6 NAME] Falling back to:', userName || 'User');
          setDisplayName(userName || 'User');
          console.log('═══════════════════════════════════════════════════════');
          return;
        }

        console.log('[V263.6 NAME] ✅ DATA RECEIVED:');
        console.log('[V263.6 NAME] Raw data:', JSON.stringify(data, null, 2));
        console.log('───────────────────────────────────────────────────────');
        console.log('[V263.6 NAME] 🔍 Name resolution:');
        console.log('[V263.6 NAME]   1. data.name:', data.name || '(empty/null)');
        console.log('[V263.6 NAME]   2. data.email:', data.email || '(empty/null)');
        console.log('[V263.6 NAME]   3. userName prop:', userName || '(empty/null)');
        console.log('[V263.6 NAME]   4. Fallback:', 'User');

        // V334-RA06: user.name is single source of truth for display
        const resolvedName = data.name || userName || 'User';

        console.log('───────────────────────────────────────────────────────');
        console.log('[V263.6 NAME] ✅ RESOLVED NAME:', resolvedName);

        if (resolvedName === 'profile') {
          console.warn('═══════════════════════════════════════════════════════');
          console.warn('[V263.6 NAME] ⚠️⚠️⚠️ WARNING ⚠️⚠️⚠️');
          console.warn('[V263.6 NAME] Database actually contains "profile" as name!');
          console.warn('[V263.6 NAME] This is a DATA ISSUE, not a CODE ISSUE');
          console.warn(`[V263.6 NAME] Fix with SQL: UPDATE users SET name = 'Your Actual Name' WHERE id = '${userId}';`);
          console.warn('═══════════════════════════════════════════════════════');
        }

        console.log('[V263.6 NAME] 💾 Setting displayName state to:', resolvedName);
        setDisplayName(resolvedName);

        console.log('═══════════════════════════════════════════════════════');
        console.log('[V263.6 NAME] ✅ FETCH COMPLETE - SUCCESS');
        console.log('[V263.6 NAME] Header should now show:', `"${resolvedName}'s Photo"`);
        console.log('═══════════════════════════════════════════════════════');

      } catch (err: unknown) {
        const caughtError = err instanceof Error ? err : new Error(String(err));
        console.error('═══════════════════════════════════════════════════════');
        console.error('[V263.6 NAME] ❌ EXCEPTION CAUGHT:');
        console.error('[V263.6 NAME] Exception type:', caughtError.constructor.name || 'Unknown');
        console.error('[V263.6 NAME] Exception message:', caughtError.message || 'No message');
        console.error('[V263.6 NAME] Exception stack:', caughtError.stack || 'No stack');
        console.error('[V263.6 NAME] Falling back to:', userName || 'User');
        setDisplayName(userName || 'User');
        console.error('═══════════════════════════════════════════════════════');
      }
    };

    fetchUserName();
    // V263.6: only userId may trigger the guarded database fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // V263.7: CRITICAL FIX - Update displayName when userName prop changes
  // The above useEffect only depends on userId and has hasAttemptedFetch guard,
  // so if userName is undefined initially and later updates, displayName never updates.
  // This secondary effect ensures displayName reflects the userName prop when it becomes valid.
  useEffect(() => {
    if (userName &&
        userName !== 'profile' &&
        userName !== 'User' &&
        userName !== 'Loading...' &&
        userName.trim().length > 0) {
      console.log('[V263.7 NAME] ✅ userName prop updated to valid value:', userName);
      setDisplayName(userName);
    }
  }, [userName]); // Watch userName prop changes

  // V213.5: Removed useEffect - useLayoutEffect above handles ref freezing, renderViewerSrc handles display

  // V250: Start upload ONLY after dialog is unmounted (architecturally prevents dialog from seeing PhotoStore updates)
  useEffect(() => {
    // Only run when dialog is closed AND we have a pending replace file
    if (!showConfirmation && pendingReplaceFileRef.current) {
      const pending = pendingReplaceFileRef.current;
      pendingReplaceFileRef.current = null; // Clear immediately to prevent re-runs

      // Dialog is now definitely unmounted, safe to start upload + PhotoStore commits
      void (async () => {
        try {
          const cycleId = uploadCycleRef.current;
          console.log(`✅ V255.${cycleId}: Starting upload after dialog unmount`);

          // V250: Resolve user ID if not available sync
          let resolvedUserId = pending.userId;
          if (!resolvedUserId) {
            const { data: { user } } = await supabase.auth.getUser();
            resolvedUserId = user?.id || '';
            if (!resolvedUserId) {
              console.log('V250_VIEWER: No user ID available');
              toast.error('Please log in to upload photos');
              isClosingRef.current = false;
              return;
            }
          }

          console.log('V250_VIEWER: User ID confirmed:', resolvedUserId);
          // V258.4: No oldPublicId tracking - overwrite handles cleanup automatically

          // V258.7: Enhanced logging for upload start
          console.log(`🎯 V258.7.${cycleId}: Marking upload in progress`);
          console.log(`🎯 V258.7.${cycleId}: userId:`, resolvedUserId);

          // V258.2: STEP 2 - Mark upload in progress (state: PREVIEWING → UPLOADING)
          try {
            v258StartUpload(resolvedUserId);
            console.log(`✅ V258.7.${cycleId}: v258StartUpload completed (state: UPLOADING)`);
          } catch (error) {
            console.error(`❌ V258.7.${cycleId}: Failed to mark upload in progress:`, error);
            throw error;
          }

          // Show toast notification (outside dialog, on profile page)
          toast.loading(
            <span dir={direction} className="text-start ps-8">{t('camera.uploading')}</span>,
            { id: 'photo-upload' }
          );

          // V329: dataUrl must always be cooked (data:image/*;base64,...) - never file:// or content://
          const uploadDataUrl = pending.dataUrl;

          // V329: Validate data URL format
          if (!uploadDataUrl.startsWith('data:image/')) {
            console.error('[V264.1] ❌ Invalid data URL format:', uploadDataUrl.substring(0, 50));
            const errorMsg = 'Invalid image format. Please try again.';
            toast.error(errorMsg, { id: 'photo-upload', duration: 10000 });
            throw new Error(errorMsg);
          }

          const { uploadExistingPhoto } = await import('../services/StandardizedPhotoUpload');
          let result: { secure_url: string; public_id: string };

          // V263.8: Get userName for Cloudinary metadata (use displayName if valid, else userName prop, else 'User')
          const userNameForUpload = (displayName && displayName !== 'Loading...' && displayName !== 'User')
            ? displayName
            : (userName || 'User');
          console.log(`[V264.1] Uploading with userName: ${userNameForUpload}`);

          try {
            result = await uploadExistingPhoto(uploadDataUrl, resolvedUserId, userNameForUpload);
            console.debug('[V329] cloudinary upload ok');
          } catch (uploadError: unknown) {
            // V261.3: Show clear error toast with actual error message
            const errorMessage = uploadError instanceof Error ? uploadError.message : 'Unknown upload error';
            console.error('[V264.1] ❌ Upload error:', errorMessage, uploadError);
            toast.error(`Upload failed: ${errorMessage}`, { id: 'photo-upload', duration: 10000 });
            throw uploadError;
          }
          
          // V261.2: Wrap result for compatibility
          const uploadResult = {
            success: true,
            photoUrl: result.secure_url,
            publicId: result.public_id,
          };

          if (!uploadResult.success || !uploadResult.photoUrl) {
            throw new Error('Upload returned invalid result');
          }

          // V261.2: Instrumentation - log public ID and URL
          console.log(`✅ V261.2.${cycleId}: Upload successful:`, {
            publicId: uploadResult.publicId,
            url: uploadResult.photoUrl.substring(0, 80),
            success: uploadResult.success,
          });

          // V256: Verify result has required data
          if (!uploadResult.success || !uploadResult.photoUrl || !uploadResult.publicId) {
            throw new Error(`Upload result incomplete: success=${uploadResult.success}, photoUrl=${!!uploadResult.photoUrl}, publicId=${!!uploadResult.publicId}`);
          }

          // V256: Canonical URL is ALWAYS the Cloudinary secure URL from result (never blob/preview)
          const canonicalUrl = uploadResult.photoUrl;

          // V265.3: Removed cache-buster creation - not needed
          // ProfilePhotoViewer doesn't need to force image refresh
          // Browser handles caching correctly with clean Cloudinary URLs

          console.log(`📋 V265.3.${cycleId}: URL generation:`, {
            canonicalUrl: canonicalUrl.substring(0, 80),
          });

          // V263.4: TRUST UPLOADWITHVERIFY.TS - No redundant database query
          // uploadWithVerify.ts (called by uploadExistingPhoto) already:
          // 1. Updated database with .select().single()
          // 2. Verified the returned row has correct photo_urls and profile_image_public_id
          // 3. Retried if needed (200ms, 400ms, 600ms exponential backoff)
          // 4. Threw error if verification failed
          // If we got here, verification PASSED. Redundant query causes race condition.
          console.log(`═══════════════════════════════════════════════════════`);
          console.log(`✅ V263.4.${cycleId}: VERIFICATION (TRUSTED)`);
          console.log(`═══════════════════════════════════════════════════════`);
          console.log(`✅ V263.4.${cycleId}: Upload verified by uploadWithVerify.ts`);
          console.log(`✅ V263.4.${cycleId}: publicId: ${uploadResult.publicId}`);
          console.log(`✅ V263.4.${cycleId}: URL: ${canonicalUrl.substring(0, 80)}`);
          console.log(`✅ V263.4.${cycleId}: Database commit verified by upload service`);
          console.log(`ℹ️ V263.4.${cycleId}: Skipping redundant query (prevents race condition)`);
          console.log(`═══════════════════════════════════════════════════════`);

          // V263.4: Database commit already verified - proceed with UI update
          const dbCommitSuccess = true;

          // V265.3: Clear state for new photo
          console.log(`[V265.3] ═══ STATE RESET START ═══`);

          // V265.3: Step 1 - Clear displayed photo URL to force re-render
          setDisplayedPhotoUrl(null);
          setImageLoaded(false);
          setImageError(false);
          setCurrentPublicId(null);
          viewerSrcAtOpen.current = null;

          console.log(`[V265.3] ✅ State cleared, waiting 100ms...`);

          // V265.3: Step 2 - Wait for state to clear (force React to process)
          await new Promise(resolve => setTimeout(resolve, 100));

          // V265.3: Step 3 - Set new photo with clean canonical URL (no cache-buster)
          console.log(`[V265.3] Setting canonical URL:`, canonicalUrl.substring(0, 100));

          setDisplayedPhotoUrl(canonicalUrl);
          setCurrentPublicId(uploadResult.publicId);

          // V267.2: Increment image key to force remount with new photo
          imageKeyRef.current += 1;
          console.log(`[V267.2] Image key incremented to ${imageKeyRef.current}`);

          // V265.3: Update frozen ref with canonical URL
          const newViewerSrc = getViewerImageSrc(canonicalUrl);
          if (newViewerSrc && !newViewerSrc.startsWith('blob:')) {
            viewerSrcAtOpen.current = newViewerSrc;
          }

          console.log(`✅ V264.3: PhotoViewer UI updated with fresh Cloudinary URL (publicId: ${uploadResult.publicId})`);

          // V258.4: No need to destroy old asset - overwrite handles it automatically!
          // Cloudinary's overwrite:true replaces the existing image at the same public_id
          // This eliminates the 403 errors and prevents storage leaks
          console.log(`ℹ️ V258.4.${cycleId}: Old asset cleanup handled by Cloudinary overwrite (no manual destroy needed)`);

          // V258.7: Enhanced logging and defensive checks for PhotoStore state transition
          console.log(`🎯 V258.7.${cycleId}: ========================================`);
          console.log(`🎯 V258.7.${cycleId}: Calling v258SetUploaded`);
          console.log(`🎯 V258.7.${cycleId}: userId:`, resolvedUserId);
          console.log(`🎯 V258.7.${cycleId}: remoteUrl:`, canonicalUrl.substring(0, 80));
          console.log(`🎯 V258.7.${cycleId}: URL is remote:`, canonicalUrl.startsWith('http'));

          // V258.2: STEP 3 - Set uploaded photo ATOMIC (state: UPLOADING → UPLOADED)
          // This clears blob, sets remote URL, transitions state, and verifies
          try {
            await v258SetUploaded(resolvedUserId, canonicalUrl);
            console.log(`✅ V258.7.${cycleId}: v258SetUploaded completed successfully`);
            
            // V258.7: Verify state transition succeeded
            const { getPhoto } = await import('../lib/photoStore');
            const verifyUrl = await getPhoto(resolvedUserId);
            const isRemote = verifyUrl?.startsWith('http');
            const isBlob = verifyUrl?.startsWith('blob:');
            
            console.log(`🔍 V258.7.${cycleId}: Post-upload verification:`, {
              returnedUrl: verifyUrl?.substring(0, 80),
              isRemote,
              isBlob,
              correct: isRemote && !isBlob,
            });
            
            if (isBlob || !isRemote) {
              console.error(`❌ V258.7.${cycleId}: CRITICAL - getPhoto still returning blob/incorrect URL after v258SetUploaded!`);
              throw new Error('PhotoStore state transition failed - still returning blob URL');
            }
            
            console.log(`✅ V258.7.${cycleId}: PhotoStore state verified - returning remote URL`);
            console.log(`🎯 V258.7.${cycleId}: ========================================`);
          } catch (v258Err) {
            console.error(`❌ V258.7.${cycleId}: v258SetUploaded failed:`, v258Err);
            console.error(`❌ V258.7.${cycleId}: Error details:`, {
              message: v258Err instanceof Error ? v258Err.message : String(v258Err),
              stack: v258Err instanceof Error ? v258Err.stack : undefined,
            });
            throw v258Err; // Re-throw to trigger error handling
          }

          // V256: COMPREHENSIVE PROFILE REFRESH - Update all caches and contexts
          try {
            // 1. Update unified photo hook (updates PhotoContext, NetworkDataContext)
            await updateUnifiedPhoto(canonicalUrl);
            console.log(`✅ V256.${cycleId}: Unified photo hook updated (PhotoContext + NetworkDataContext)`);

            // 2. Update NetworkDataContext currentUser cache directly
            if (updateCurrentUser) {
              await updateCurrentUser({ photo_urls: [canonicalUrl] });
              console.log(`✅ V256.${cycleId}: NetworkDataContext currentUser updated`);
            }

            // 3. Clear optimistic override (PhotoStore URL now takes precedence)
            const { clearOverride } = await import('../lib/uiPhotoOverride');
            clearOverride(resolvedUserId);
            console.log(`✅ V256.${cycleId}: Optimistic override cleared`);

            // 4. V265.3: Notify parent with clean canonical URL (no cache-buster)
            // V265.3: Preload image before callback for instant display
            if (onPhotoUpdate) {
              console.log(`[V265.3 Viewer] Preloading uploaded image...`);

              // V265.3: Preload image to browser cache
              const img = new Image();
              img.src = canonicalUrl;
              img.onload = () => {
                console.log(`[V265.3 Viewer] ✅ Image preloaded to cache`);
              };
              img.onerror = () => {
                console.warn(`[V265.3 Viewer] ⚠️ Preload failed (non-critical)`);
              };

              // V265.3: Call parent callback immediately (don't wait for preload)
              console.log(`[V265.3 Viewer] Calling onPhotoUpdate with:`, canonicalUrl.substring(0, 80));
              onPhotoUpdate(canonicalUrl); // V265.3: Use clean URL without cache-buster
              console.log(`✅ V265.3.${cycleId}: Parent notified with clean canonical URL`);
            }

            // 5. Update globalDataStore (if used)
            try {
              const { globalDataStore } = await import('../lib/globalDataStore');
              const curr = globalDataStore.getProfile() ?? {};
              globalDataStore.updateProfile({
                ...curr,
                id: resolvedUserId,
                photo_urls: [canonicalUrl],
              });
              console.log(`✅ V256.${cycleId}: GlobalDataStore updated`);
            } catch (globalStoreErr) {
              console.warn(`⚠️ V256.${cycleId}: GlobalDataStore update failed (non-critical):`, globalStoreErr);
            }

            console.log(`✅ V256.${cycleId}: All caches and contexts refreshed`);
          } catch (refreshErr) {
            console.error(`❌ V256.${cycleId}: Profile refresh error:`, refreshErr);
            // Don't throw - DB commit succeeded, refresh is best-effort
          }

          // V265.3: Final state update with clean canonical URL (no cache-buster needed)
          console.log(`[V265.3] Setting final canonical URL...`);
          setDisplayedPhotoUrl(canonicalUrl);

          // V265.3: CRITICAL - Store uploaded URL for next snapshot
          // This ensures "Current Photo" in next replace dialog shows THIS photo
          console.log('[V265.3] Storing uploaded URL as reference for next snapshot');
          console.log('[V265.3] Canonical URL:', canonicalUrl.substring(0, 80));

          // V265.3: Keep reference to uploaded URL
          viewerSrcAtOpen.current = getViewerImageSrc(canonicalUrl);

          console.log('[V265.3] ✅ Next snapshot will use:', viewerSrcAtOpen.current?.substring(0, 80));
          console.log(`[V265.3] ═══ UPLOAD COMPLETE ✅ ═══`);

          // V256: Update toast to success ONLY after DB verification passed
          toast.success(
            <span dir={direction} className="text-start ps-8">{t('camera.photoUpdateSuccess')}</span>,
            { id: 'photo-upload' }
          );
          console.log(`✅ V256.${cycleId}: Success toast shown - DB verified, all caches refreshed`);

        } catch (error) {
          console.error('V250_VIEWER: Upload failed:', error);
          
          // V250: Clear optimistic override on error (revert to old photo)
          const { clearOverride } = await import('../lib/uiPhotoOverride');
          if (pending.userId) {
            clearOverride(pending.userId);
            console.log('✅ V250: Optimistic override cleared on error - reverting to old photo');
          }
          
          toast.error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`, { id: 'photo-upload' });
        } finally {
          // V254: Release upload lock
          const cycleId = uploadCycleRef.current;
          uploadInFlightRef.current = false;
          setUploadInFlight(false);
          console.log(`🔓 V255.${cycleId}: Upload lock released`);
          
          // V250: Reset closing flag
          isClosingRef.current = false;
          
          console.log(`✅ V255.${cycleId}: Upload cycle complete`);
        }
      })();
    }
    // Upload confirmation is edge-triggered; adding mutable render helpers can repeat a commit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showConfirmation, userName, onPhotoUpdate, updateUnifiedPhoto, t, direction]);

  // V144.1: Action handlers with aspect ratio preservation
  const handleDownload = async () => {
    try {
      // V144.1: Use unified service for consistent aspect ratio transformation
      const downloadUrl = photoUrl; // Use direct URL for download

      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${userName}_BlissNet.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('V144.1: Downloaded image with square aspect ratio (1080x1080)');
      console.log('V144.1: Download URL:', downloadUrl);
    } catch (error) {
      console.error('V144.1: Download failed:', error);
    }
  };

  const handleUpload = async () => {
    if (uploadInFlightRef.current) {
      toast.info('Please wait for current upload to complete');
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    const currentUserId = userId || user?.id;

    if (!currentUserId) {
      toast.error('Please log in to upload photos');
      return;
    }

    // V329: Native Base64 Picker - NEVER fetch content:// on Samsung
    if (Capacitor.isNativePlatform()) {
      console.debug('[V329] nativePick start');
      try {
        const photo = await CapacitorCamera.getPhoto({
          source: CameraSource.Photos,
          resultType: CameraResultType.Base64,
          quality: 90,
          allowEditing: false,
        });

        if (!photo.base64String) {
          console.debug('[V329] sizeReject / invalidDataUrl: base64 missing');
          toast.error('Could not read photo. Please try again.', { id: 'read-error' });
          return;
        }

        const fmt = (photo.format || 'jpeg').toLowerCase();
        const mime = fmt === 'jpg' ? 'jpeg' : fmt;
        const cooked = `data:image/${mime};base64,${photo.base64String}`;

        if (!cooked.startsWith('data:image/')) {
          console.debug('[V329] invalidDataUrl');
          toast.error('Invalid image format. Please try again.', { id: 'read-error' });
          return;
        }

        const approxBytes = Math.floor(photo.base64String.length * 0.75);
        if (approxBytes > MAX_BLOB_BYTES) {
          console.debug('[V329] sizeReject');
          toast.error(`Photo too large (${(approxBytes / 1024 / 1024).toFixed(1)}MB). Max 8MB.`, { id: 'read-error' });
          return;
        }

        console.debug('[V329] nativePick success');
        const cycleId = uploadCycleRef.current + 1;
        uploadCycleRef.current = cycleId;

        const currentPhoto = photoUrl || displayedPhotoUrl || displayPhotoUrl || null;
        captureSnapshot(currentPhoto);

        viewerSrcAtOpen.current = null;
        flushSync(() => {
          setDialogKilled(false);
          setImageLoaded(false);
          setImageError(false);
          setShowConfirmation(false);
          setDisplayedPhotoUrl(null);
          setNewImageDataUrl(null);
        });

        flushSync(() => {
          setNewImageDataUrl(cooked);
          setNewImagePreview(cooked);
          setNewImageFile(null);
          setShowConfirmation(true);
        });
      } catch (e) {
        console.debug('[V329] nativePick error:', e);
        if ((e as Error)?.message?.toLowerCase().includes('cancel')) return;
        toast.error('Could not read photo. Please try again.', { id: 'read-error' });
      }
      return;
    }

    // Web: file input
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        // V213.5: Revoke old preview blob URL if exists (one URL per action)
        if (previewUrlRef.current && previewUrlRef.current.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(previewUrlRef.current);
          } catch (e) {
            // ignore
          }
        }

        // V213.5: Create local blob URL (independent from global manager - prevents revocation conflicts)
        const previewUrl = URL.createObjectURL(file);
        previewUrlRef.current = previewUrl;

        // Validate the blob URL
        if (!previewUrl || !previewUrl.startsWith('blob:')) {
          toast.error('Failed to create image preview');
          previewUrlRef.current = null;
          return;
        }

        // Test if the blob URL is actually loadable
        const testImg = new Image();
        testImg.onload = () => {
          // V255: Increment upload cycle counter
          uploadCycleRef.current += 1;
          const cycleId = uploadCycleRef.current;
          console.log(`🚀 V255.${cycleId}: Starting upload cycle`);

          // V255: CRITICAL - Reset viewer frozen src BEFORE opening dialog
          // This prevents the viewer from keeping a stale frozen URL across cycles
          console.log(`🔄 V255.${cycleId}: Resetting viewer frozen src`);
          viewerSrcAtOpen.current = null;

          // V255: ATOMIC reset using flushSync (prevents React batching issues)
          // V323: Clear newImageDataUrl to prevent stale selfie data when switching to Upload
          console.log(`✅ V255.${cycleId}: Resetting state (atomic)`);
          flushSync(() => {
            setDialogKilled(false);  // CRITICAL: Reset hard-kill flag
            setImageLoaded(false);   // Reset image loading state
            setImageError(false);    // Reset error state
            setShowConfirmation(false); // Ensure clean close/open cycle
            setDisplayedPhotoUrl(null); // V255: Reset displayed URL
            setNewImageDataUrl(null);   // V323: Clear stale selfie data when switching to Upload
          });

          // V275.7: CRITICAL FIX - Use photoUrl PROP first (from Profile's DB data)
          // photoUrl comes from Profile's fresh DB fetch (has correct Cloudinary URL)
          // displayedPhotoUrl is for optimistic updates after upload
          // displayPhotoUrl may have stale hook data (old Google avatar)
          const currentPhoto = photoUrl || displayedPhotoUrl || displayPhotoUrl || null;
          console.log('[V275.7] Capturing snapshot:', {
            photoUrl: photoUrl?.substring(0, 60),
            displayedPhotoUrl: displayedPhotoUrl?.substring(0, 60),
            displayPhotoUrl: displayPhotoUrl?.substring(0, 60),
            selected: currentPhoto?.substring(0, 60)
          });
          captureSnapshot(currentPhoto);

          // V255: ATOMIC dialog open using flushSync
          // V323: Clear newImageDataUrl so handleConfirmReplace reads from file, not stale selfie
          flushSync(() => {
            setNewImageFile(file);
            setNewImagePreview(previewUrl);
            setNewImageDataUrl(null);  // V323: Ensure Upload flow uses file, not old selfie data
            setShowConfirmation(true);
          });

          console.log(`✅ V255.${cycleId}: Dialog opened (atomic)`);
        };
        testImg.onerror = () => {
          if (previewUrlRef.current === previewUrl) {
            URL.revokeObjectURL(previewUrl);
            previewUrlRef.current = null;
          }
          toast.error('Failed to process image. Please try again.');
        };
        testImg.src = previewUrl;
      }
    };
    fileInput.click();
  };

  // V250: Hard-kill flag to prevent dialog from rendering after confirm
  const [dialogKilled, setDialogKilled] = useState(false);

  // V250: handleConfirmReplace - closes dialog immediately, stores pending file for useEffect
  const handleConfirmReplace = async () => {
    const cycleId = uploadCycleRef.current;
    console.log(`🚀 V275.12.${cycleId}: handleConfirmReplace called`);

    // V275.12: Try to get dataUrl - either from state (Android camera) or read from file (web)
    let fileDataUrl = newImageDataUrl;
    const frozenCurrentPhoto = currentPhotoAtOpen; // Snapshot captured when dialog opened
    const currentUserId = userId; // Capture userId prop

    // V275.12 / V323: If no dataUrl stored, use native-friendly conversion (fetch+blob on Android)
    let conversionStrategy: string | null = null;
    if (!fileDataUrl && (newImageFile || newImagePreview)) {
      console.log('[V323] No stored dataUrl, using fileOrUriToDataUrl (native-friendly)');
      try {
        const converted = await fileOrUriToDataUrl(newImageFile, newImagePreview);
        if (converted) {
          fileDataUrl = converted.dataUrl;
          conversionStrategy = converted.strategy;
          console.log('[V323] ✅ Conversion complete:', {
            strategy: conversionStrategy,
            dataUrlLength: fileDataUrl.length,
          });
        } else {
          toast.error('Could not process image. Please try again.', { id: 'read-error' });
          return;
        }
      } catch (readError: unknown) {
        const errorMessage = readError instanceof Error ? readError.message : String(readError);
        console.error('[V324] ❌ Conversion failed:', errorMessage);
        toast.error(`Read failed: ${errorMessage}`, { id: 'read-error' });
        return;
      }
    }

    // V323: Defensive logging - log source and length after all dataUrl resolution
    const dataUrlSource = conversionStrategy ?? (fileDataUrl ? 'from-state (initialDataUrl)' : 'none');
    console.log('[V323] handleConfirmReplace dataUrl resolved:', {
      length: fileDataUrl?.length ?? 0,
      strategy: dataUrlSource,
    });

    if (!fileDataUrl || !fileDataUrl.startsWith('data:image/')) {
      console.error('[V275.12] ❌ No dataUrl available and no file to read');
      toast.error('No image data. Please try again.');
      return;
    }

    if (!currentUserId) {
      console.error('[V275.12] ❌ No userId');
      toast.error('User ID missing. Please try again.');
      return;
    }

    console.log(`[V275.12] ✅ Using dataUrl for upload, length: ${fileDataUrl.length}`);

    // V275.12: Set upload lock immediately
    uploadInFlightRef.current = true;
    setUploadInFlight(true);
    console.log(`[V275.12] 🔒 Upload lock acquired`);

    // V258: STEP 1 - Set preview blob in V258 PhotoStore (state: none → PREVIEWING)
    let previewUrlForOptimistic = newImagePreview;
    if (!previewUrlForOptimistic && newImageFile) {
      // Revoke old preview if exists
      if (previewUrlRef.current && previewUrlRef.current.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(previewUrlRef.current);
        } catch (e) {
          // ignore
        }
      }
      // V275.13: Use newImageFile instead of undefined fileToUpload
      previewUrlForOptimistic = URL.createObjectURL(newImageFile);
      previewUrlRef.current = previewUrlForOptimistic;
    }

    // V258.2: Set preview blob (state: none → PREVIEWING)
    if (currentUserId && fileDataUrl) {
      try {
        const blob = newImageFile
          ? new Blob([newImageFile], { type: newImageFile.type })
          : await (await fetch(fileDataUrl)).blob();
        await v258SetPreview(currentUserId, blob);
        console.log(`✅ V258.2.${cycleId}: Preview blob set (state: PREVIEWING)`);
      } catch (error) {
        console.error(`❌ V258.2.${cycleId}: Failed to set preview blob:`, error);
      }
    }

    // V250: Set optimistic override BEFORE unmount (shows new image instantly on profile)
    const { setOverride } = await import('../lib/uiPhotoOverride');
    if (currentUserId && previewUrlForOptimistic) {
      setOverride(currentUserId, previewUrlForOptimistic);
      console.log('[V275.7] ✅ Optimistic override set');
    }

    // V213.5: Show preview immediately in viewer image (before upload)
    if (previewUrlForOptimistic) {
      setImageLoaded(false);
      setImageError(false);
      const previewSrc = getViewerImageSrc(previewUrlForOptimistic);
      viewerSrcAtOpen.current = previewSrc;
      setDisplayedPhotoUrl(previewUrlForOptimistic);
      console.log('[V275.7] ✅ Preview shown in viewer');
    }

    // V275.7: Store dataUrl (not File) for useEffect (after dialog unmounts)
    pendingReplaceFileRef.current = {
      dataUrl: fileDataUrl,  // V275.7: dataUrl read BEFORE any state changes
      previewUrl: previewUrlForOptimistic,
      userId: currentUserId || '', // Will be resolved in useEffect if empty
    };

    // V250: Hard-kill dialog rendering immediately (prevents any repaint)
    flushSync(() => {
      setDialogKilled(true); // CRITICAL: Hard-kill flag prevents any render
      setShowConfirmation(false);
      setNewImagePreview(null); // CRITICAL: Prevents dialog from rendering again
      setNewImageFile(null);
      setCurrentPhotoAtOpen(null);
    });

    console.log(`✅ V255.${cycleId}: Dialog closed, upload will start in useEffect`);

    // V254: Only navigate on Android (web stays in viewer)
    if (Capacitor.isNativePlatform()) {
      console.log(`📱 V255.${cycleId}: Android detected, navigating to profile`);
      navigate('/profile', { replace: true });
    } else {
      console.log(`🌐 V255.${cycleId}: Web detected, staying in viewer`);
    }

    // V250: Upload will start in useEffect when showConfirmation becomes false
    // This ensures dialog is unmounted before any PhotoStore commits
  };

  const handleCancelReplace = async () => {
    const cycleId = uploadCycleRef.current;
    console.log(`❌ V255.${cycleId}: Upload cancelled by user`);
    // V213.5: Revoke local preview blob URL (independent from global manager)
    if (previewUrlRef.current && previewUrlRef.current.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(previewUrlRef.current);
        console.log('✅ V213.5: Revoked local preview blob URL on cancel');
      } catch (e) {
        // ignore
      }
    }
    previewUrlRef.current = null;

    // V255: ATOMIC reset using flushSync (critical for next upload)
    flushSync(() => {
      setDialogKilled(false); // CRITICAL: Reset hard-kill flag
      setShowConfirmation(false);
      setNewImageFile(null);
      setNewImagePreview(null);
      setCurrentPhotoAtOpen(null);
    });

    // V255: Reset refs (not state)
    isClosingRef.current = false;
    uploadInFlightRef.current = false;
    setUploadInFlight(false);

    console.log(`✅ V255.${cycleId}: State reset (atomic), ready for next upload`);
  };

  const handleSelfie = () => {
    console.log('Selfie button clicked - open camera');
    if (onEdit) {
      onEdit();
    } else {
      console.warn('onEdit prop not provided to ProfilePhotoViewer');
    }
  };


  return (
    <div dir={direction} className="fixed inset-0 z-50 bg-[#0a0a0a] flex flex-col overflow-hidden">
      {/* 1. HEADER: Flex-none row with Safe Area Top padding */}
      <header 
        className="flex-none w-full bg-black/40 backdrop-blur-md border-b border-white/5 z-20"
        style={{ paddingTop: 'var(--sat, 20px)' }}
      >
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex flex-col text-start">
            <span className="text-[10px] text-white/40 uppercase tracking-widest font-bold"><bdi dir="ltr">BlissNet</bdi></span>
            <h1 className="text-white text-lg font-medium tracking-tight text-start">
              {displayName.includes('Loading') ? t('camera.userPhoto') : (
                <Trans
                  i18nKey="camera.profilePhotoOwner"
                  values={{ name: displayName }}
                  components={{ owner: <bdi dir="auto" /> }}
                />
              )}
            </h1>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white transition-all active:scale-90"
            title={t('camera.closeViewer')}
            aria-label={t('camera.closeViewer')}
          >
            <X size={20} />
          </button>
        </div>
      </header>

      {/* 2. IMAGE AREA: Flex-1 centered 4:5 portrait photo */}
      <main className="flex-1 min-h-0 relative flex items-center justify-center p-4" onClick={onClose}>
        <div 
          className="relative w-full max-w-[90vw] md:max-w-[336px] aspect-[4/5] rounded-3xl overflow-hidden shadow-[0_32px_64px_-16px_rgba(0,0,0,0.8)] border border-white/10 bg-black/20"
          onClick={(e) => e.stopPropagation()}
        >
          {renderViewerSrc && (renderViewerSrc.startsWith('http') || renderViewerSrc.startsWith('blob:') || renderViewerSrc.startsWith('data:')) ? (
            <img
              key={`photo-${imageKeyRef.current}`}
              src={renderViewerSrc}
              alt={t('camera.profilePhotoAlt')}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              style={{
                objectPosition: 'center 20%',
                filter: 'contrast(1.02) brightness(1.01) saturate(1.05)'
              }}
              onLoad={() => setImageLoaded(true)}
              onError={async (e) => {
                const img = e.currentTarget;
                const failedSrc = img.src;
                console.warn(`⚠️ V258.3: ProfilePhotoViewer image load failed:`, failedSrc.substring(0, 80));

                // V258.3: If blob URL fails, it might be revoked - this shouldn't happen with state machine
                if (failedSrc.startsWith('blob:')) {
                  console.error('❌ V258.3: Blob URL failed in viewer - PhotoStore state machine error!');
                  
                  // Clear blob cache/override
                  try {
                    const { clearOverride } = await import('../lib/uiPhotoOverride');
                    clearOverride(userId || '');
                    console.log('✅ V253: Cleared blob override from viewer');
                    
                    // Clear PhotoStore blob entry
                    const { invalidate } = await import('../lib/photoStore');
                    if (userId) {
                      await invalidate(userId);
                      console.log('✅ V253: Cleared PhotoStore blob from viewer');
                    }
                  } catch (err) {
                    console.warn('⚠️ V253: Failed to clear blob cache from viewer:', err);
                  }
                  
                  // V253: Try to get remote URL from multiple sources and persist as canonical
                  let remoteUrl: string | null = null;
                  
                  // Priority 1: displayPhotoUrl if it's not a blob
                  if (displayPhotoUrl && !displayPhotoUrl.startsWith('blob:')) {
                    remoteUrl = displayPhotoUrl;
                  }
                  
                  // Priority 2: Fetch from DB if userId available
                  if (!remoteUrl && userId) {
                    try {
                      const { data: authData } = await supabase.auth.getUser();
                      let userData: { photo_urls?: unknown } | null = null;
                      if (authData?.user?.id === userId) {
                        const r = await supabase.from('users').select('photo_urls').eq('id', userId).maybeSingle();
                        userData = r.data;
                      } else {
                        const { rpcPeerUsersForViewer } = await import('@/lib/d001PeerUsersRpc');
                        const { data: rows } = await rpcPeerUsersForViewer(supabase, [userId], { includePrefs: false });
                        userData = rows?.[0] ?? null;
                      }
                      
                      if (userData?.photo_urls && Array.isArray(userData.photo_urls) && userData.photo_urls.length > 0) {
                        const firstUrl = userData.photo_urls[0];
                        if (typeof firstUrl === 'string' && firstUrl.startsWith('http')) {
                          remoteUrl = firstUrl;
                          console.log('✅ V253: Fetched remote URL from DB:', remoteUrl.substring(0, 80));
                        }
                      }
                    } catch (dbError) {
                      console.warn('⚠️ V253: Failed to fetch remote URL from DB:', dbError);
                    }
                  }
                  
                  // If we have a remote URL, use it and persist as canonical
                  if (remoteUrl) {
                    // V253: Persist Cloudinary URL as canonical cache (not blob)
                    try {
                      const { setProfilePhotoCachePayload } = await import('../lib/profilePhotoKeys');
                      setProfilePhotoCachePayload(userId || '', {
                        kind: 'url',
                        url: remoteUrl,
                        timestamp: Date.now(),
                        ttl: 24 * 60 * 60 * 1000,
                      });
                      console.log('✅ V253: Persisted Cloudinary URL as canonical cache');
                    } catch (cacheError) {
                      console.warn('⚠️ V253: Failed to persist canonical cache:', cacheError);
                    }
                    
                    const cloudinarySrc = getViewerImageSrc(remoteUrl);
                    viewerSrcAtOpen.current = cloudinarySrc;
                    img.src = cloudinarySrc;
                    setImageError(false); // Clear error state
                    console.log('✅ V253: Viewer fallback to Cloudinary URL:', cloudinarySrc.substring(0, 80));
                  } else {
                    // No Cloudinary URL available, show placeholder
                    setImageError(true);
                  }
                } else {
                  setImageError(true);
                }
              }}
            />
          ) : (
            <PhotoPreviewEmpty message={t('camera.noPhoto')} />
          )}

          {/* Loader Overlay */}
          {!imageLoaded && renderViewerSrc && !imageError && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-white" />
            </div>
          )}

          {/* No photo state */}
          {!displayPhotoUrl && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800 text-white">
              <div className="text-6xl mb-4">📷</div>
              <p className="text-lg font-semibold mb-2 text-start">{t('camera.noPhoto')}</p>
              <p className="text-sm text-gray-300 text-start px-4">
                {t('camera.noProfilePhoto')}
              </p>
            </div>
          )}
        </div>
      </main>

      {/* 3. BUTTON BAR: Flex-none at bottom with Safe Area Bottom padding */}
      <footer 
        className="flex-none w-full bg-black/60 backdrop-blur-2xl border-t border-white/5 z-20"
        style={{ paddingBottom: 'calc(var(--sab, 20px) + 1.5rem)' }}
      >
        <div className="px-6 pt-6 max-w-sm mx-auto">
          <div className="grid grid-cols-4 gap-3">
            {/* Save Button */}
            <button
              onClick={handleDownload}
              className="flex flex-col items-center justify-center gap-1.5 h-16 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 text-blue-400 transition-all active:scale-95 shadow-lg"
              title={t('camera.downloadPhoto')}
              aria-label={t('camera.downloadPhoto')}
            >
              <Download size={18} />
              <span className="text-[10px] font-bold uppercase tracking-wider">{t('common.save')}</span>
            </button>

            {/* Upload Button */}
            <button
              onClick={handleUpload}
              disabled={isUploading || uploadInFlight}
              className={`flex flex-col items-center justify-center gap-1.5 h-16 rounded-2xl bg-white/5 border border-white/10 transition-all active:scale-95 shadow-lg ${
                isUploading || uploadInFlight ? 'text-gray-600 opacity-50' : 'text-green-400 hover:bg-white/10'
              }`}
              title={isUploading ? t('camera.uploading') : t('camera.uploadPhoto')}
              aria-label={isUploading ? t('camera.uploading') : t('camera.uploadPhoto')}
            >
              <Upload size={18} className={isUploading ? 'animate-bounce' : ''} />
              <span className="text-[10px] font-bold uppercase tracking-wider">{t('camera.upload')}</span>
            </button>

            {/* Selfie Button */}
            <button
              onClick={handleSelfie}
              disabled={!isOwnProfile}
              className={`flex flex-col items-center justify-center gap-1.5 h-16 rounded-2xl bg-white/5 border border-white/10 transition-all active:scale-95 shadow-lg ${
                isOwnProfile ? 'text-purple-400 hover:bg-white/10' : 'text-gray-700 opacity-30'
              }`}
              title={t('camera.takeSelfie')}
              aria-label={t('camera.takeSelfie')}
            >
              <Camera size={18} />
              <span className="text-[10px] font-bold uppercase tracking-wider">{t('camera.selfie')}</span>
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="flex flex-col items-center justify-center gap-1.5 h-16 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 text-red-500 transition-all active:scale-95 shadow-lg"
              title={t('camera.closeViewer')}
              aria-label={t('camera.closeViewer')}
            >
              <X size={18} />
              <span className="text-[10px] font-bold uppercase tracking-wider">{t('camera.close')}</span>
            </button>
          </div>
        </div>
      </footer>

      {/* V250: Simple Replace Photo Dialog */}
      {dialogKilled ? null : (showConfirmation && newImagePreview && !isClosingRef.current ? (
        <SimpleReplacePhotoDialog
          isOpen={true}
          onClose={handleCancelReplace}
          onConfirm={handleConfirmReplace}
          newPhotoUrl={newImagePreview}
          currentPhotoUrl={currentPhotoAtOpen || (viewerSrcAtOpen.current ?? '') || getViewerImageSrc(photoUrl || unifiedPhotoUrl || '')}
          fallbackInitial={userName?.charAt(0)?.toUpperCase() || '?'}
        />
      ) : null)}
    </div>
  );
};

// V266: Memoized to prevent unnecessary re-renders
export default React.memo(ProfilePhotoViewer);
